"use client";

import { type ReactNode, useState, type FC, type Ref } from "react";
import { AnalysisGallery, type AnalysisGalleryProps } from "../AnalysisGallery";
import { AnalysisIndex, type AnalysisIndexProps } from "../AnalysisIndex";
import { AnalysisLoader, type AnalysisLoaderProps } from "../AnalysisLoader";
import {
  AnalysisObservationsSection,
  type AnalysisObservationsSectionProps,
} from "../AnalysisObservationsSection";
import type { AnalysisObservationCategory } from "../../types/AnalysisObservation";
import { AnalysisSummary, type AnalysisSummaryProps } from "../AnalysisSummary";
import { ContentMenu, type ContentMenuProps } from "../ContentMenu";
import { ContentMenuProvider } from "../ContentMenuProvider";
import { MapboxMapCard } from "../MapboxMapCard";
import {
  NeighbourhoodAnalysis,
  type NeighbourhoodAnalysisProps,
} from "../NeighbourhoodAnalysis";
import { PageBreak } from "../PageBreak";
import {
  PropertyHazardProfileAnalysis,
  type PropertyHazardProfileAnalysisProps,
} from "../PropertyHazardProfileAnalysis";
import {
  PropertyValueAnalysis,
  type PropertyValueAnalysisProps,
} from "../PropertyValueAnalysis";
import { SundialAnalysis, type SundialAnalysisProps } from "../SundialAnalysis";
import {
  Container,
  Flex,
  GalleryCarousel,
  type GalleryCarouselProps,
  Heading,
  type HouseRecord,
  Modal,
  Spacing,
  Text,
  type UserPromptRecord,
} from "@huub-web/ui";

import { AnalysisObservationsModal } from "../AnalysisObservationsSection/components/AnalysisObservationsModal";
import { MessageBanner } from "./components/MessageBanner";
import { AnalysisObservationMainImage } from "./components/AnalysisObservationMainImage";
import { selectUniqueImage } from "./utils/selectUniqueImage";
import {
  analysisSectionIllustrations,
  AnalysisSectionIndex,
  analysisSectionIndexLabels,
} from "./types/AnalysisSections";
import { MapModal, type MapModalType } from "./components/MapModal";

import { contentMenuPageBreakStyle } from "./Analysis.css";

/**
 * Formats insight count with proper singular/plural handling
 * @param count - The number of insights (can be undefined)
 * @returns Formatted string like "1 inzicht" or "5 inzichten"
 */
const formatInsightCount = (
  count: number | undefined,
  singular: string = "inzicht",
  plural: string = "inzichten",
): string => {
  const safeCount = count ?? 0;
  return `${safeCount} ${safeCount === 1 ? singular : plural}`;
};

/**
 * Generic wrapper for sub-template data
 */
export type SubTemplate<T> = {
  loading?: boolean;
  data?: T;
};

export type GeoJSONType = "neighbourhood" | "district" | "foundation";

/**
 * Props for the summary section
 */
export type AnalysisSummaryData = {
  summary?: AnalysisSummaryProps["summary"];
  grade?: AnalysisSummaryProps["grade"];
};

/**
 * Props for the gallery section
 */
export type AnalysisGalleryData = {
  summary?: AnalysisGalleryProps["description"];
  images?: AnalysisGalleryProps["images"];
};

/**
 * Props for the property hazard profile section
 */
export type AnalysisPropertyHazardProfileData = {
  summary?: PropertyHazardProfileAnalysisProps["description"];
  recommendations?: PropertyHazardProfileAnalysisProps["recommendations"];
  riskLevel?: PropertyHazardProfileAnalysisProps["riskLevel"];
  riskCategory?: PropertyHazardProfileAnalysisProps["riskCategory"];
  soilType?: PropertyHazardProfileAnalysisProps["soilType"];
  soilPercentage?: PropertyHazardProfileAnalysisProps["soilPercentage"];
  derived?: boolean;
  derivedFromPc6?: string;
};

/**
 * Props for the neighbourhood section
 */
export type AnalysisNeighbourhoodData = {
  name?: NeighbourhoodAnalysisProps["name"];
  // geoJSON?: NeighbourhoodAnalysisProps["geoJSON"];
  summary: NeighbourhoodAnalysisProps["description"];
  amenities: NeighbourhoodAnalysisProps["amenities"];
};

/**
 * Props for the property value section
 */
export type AnalysisPropertyValueData = {
  // currentPrice?: PropertyValueAnalysisProps["currentPrice"];
  // squareMeterPrice?: PropertyValueAnalysisProps["squareMeterPrice"];
  wozPrices?: PropertyValueAnalysisProps["wozPrices"];
  summary?: PropertyValueAnalysisProps["description"];
};

export type AnalysisObservationsData = {
  summary: AnalysisObservationsSectionProps["description"];
  observations?: AnalysisObservationsSectionProps["observations"];
  plusPoints?: AnalysisObservationsSectionProps["plusPoints"];
  grade?: AnalysisObservationsSectionProps["grade"];
  questions?: AnalysisObservationsSectionProps["questions"];
  mainImage?: string;
};

export type GetGeoJSONHandler = (
  type: GeoJSONType,
) => Promise<GeoJSON.FeatureCollection>;

export type AnalysisProps = {
  unlisted?: boolean;
  record: HouseRecord;
  saved?: boolean;
  center: SundialAnalysisProps["center"];
  images: GalleryCarouselProps["items"];
  lockUI?: boolean;
  CTAs?: {
    maximumMortgage?: ReactNode;
    chance?: ReactNode;
    realisticOffer?: ReactNode;
  };
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
  analysis?: {
    summary?: SubTemplate<AnalysisSummaryData>;
    gallery?: SubTemplate<AnalysisGalleryData>;
    neighbourhood?: SubTemplate<AnalysisNeighbourhoodData>;
    propertyValue?: SubTemplate<AnalysisPropertyValueData>;
    propertyHazardProfile?: SubTemplate<AnalysisPropertyHazardProfileData>;
    observations?: {
      environmentAndNeighborhood?: AnalysisObservationsData;
      exteriorAndOutdoorSpaces?: AnalysisObservationsData;
      entranceAndLayout?: AnalysisObservationsData;
      technologyAndEnergy?: AnalysisObservationsData;
      safetyAndMaterials?: AnalysisObservationsData;
      generalAndLegal?: AnalysisObservationsData;
      loading?: boolean;
    };
  };
  loaderSteps?: AnalysisLoaderProps["steps"];
  /**
   * Action button for the Interior Designer entry point
   */
  interiorDesignerAction?: ReactNode;
  savable: boolean;
  mapBoxToken: string;
  contentMenuVariant?: ContentMenuProps["variant"];
  onPrompt: (prompt: UserPromptRecord) => void;
  onSaveToggle: () => void;
  getGeoJSON: GetGeoJSONHandler;
};

/**
 * Analysis component displays the analysis of a house
 */
export const Analysis: FC<AnalysisProps> = ({
  lockUI,
  CTAs,
  analysis,
  record,
  unlisted,
  center,
  images,
  mapBoxToken,
  loaderSteps,
  contentMenuVariant = "marketing",
  savable,
  saved,
  interiorDesignerAction,
  onPrompt,
  onSaveToggle,
  getGeoJSON,
  ref,
}) => {
  const [isImagesModalOpen, setImagesModalOpen] = useState(false);
  const [isObservationModalOpen, setIsObservationModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [mapModalType, setMapModalType] = useState<MapModalType>("general");

  const [activeObservationIndex, setActiveObservationIndex] = useState<
    AnalysisSectionIndex | undefined
  >(undefined);

  const activeObservationCategory: AnalysisObservationCategory | undefined =
    (() => {
      switch (activeObservationIndex) {
        case AnalysisSectionIndex.EnvironmentAndNeighborhood:
          return {
            id: "environmentAndNeighborhood",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.EnvironmentAndNeighborhood
            ],
            observations:
              analysis?.observations?.environmentAndNeighborhood?.observations,
            plusPoints:
              analysis?.observations?.environmentAndNeighborhood?.plusPoints,
            questions:
              analysis?.observations?.environmentAndNeighborhood?.questions,
            grade: analysis?.observations?.environmentAndNeighborhood?.grade,
          };
        case AnalysisSectionIndex.ExteriorAndOutdoorSpaces:
          return {
            id: "exteriorAndOutdoorSpaces",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.ExteriorAndOutdoorSpaces
            ],
            observations:
              analysis?.observations?.exteriorAndOutdoorSpaces?.observations,
            plusPoints:
              analysis?.observations?.exteriorAndOutdoorSpaces?.plusPoints,
            questions:
              analysis?.observations?.exteriorAndOutdoorSpaces?.questions,
            grade: analysis?.observations?.exteriorAndOutdoorSpaces?.grade,
          };
        case AnalysisSectionIndex.EntranceAndLayout:
          return {
            id: "entranceAndLayout",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.EntranceAndLayout
            ],
            observations:
              analysis?.observations?.entranceAndLayout?.observations,
            plusPoints: analysis?.observations?.entranceAndLayout?.plusPoints,
            questions: analysis?.observations?.entranceAndLayout?.questions,
            grade: analysis?.observations?.entranceAndLayout?.grade,
          };
        case AnalysisSectionIndex.TechnologyAndEnergy:
          return {
            id: "technologyAndEnergy",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.TechnologyAndEnergy
            ],
            observations:
              analysis?.observations?.technologyAndEnergy?.observations,
            plusPoints: analysis?.observations?.technologyAndEnergy?.plusPoints,
            questions: analysis?.observations?.technologyAndEnergy?.questions,
            grade: analysis?.observations?.technologyAndEnergy?.grade,
          };
        case AnalysisSectionIndex.SafetyAndMaterials:
          return {
            id: "safetyAndMaterials",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.SafetyAndMaterials
            ],
            observations:
              analysis?.observations?.safetyAndMaterials?.observations,
            plusPoints: analysis?.observations?.safetyAndMaterials?.plusPoints,
            questions: analysis?.observations?.safetyAndMaterials?.questions,
            grade: analysis?.observations?.safetyAndMaterials?.grade,
          };
        case AnalysisSectionIndex.GeneralAndLegal:
          return {
            id: "generalAndLegal",
            name: analysisSectionIndexLabels[
              AnalysisSectionIndex.GeneralAndLegal
            ],
            observations: analysis?.observations?.generalAndLegal?.observations,
            plusPoints: analysis?.observations?.generalAndLegal?.plusPoints,
            questions: analysis?.observations?.generalAndLegal?.questions,
            grade: analysis?.observations?.generalAndLegal?.grade,
          };
        default:
          return undefined;
      }
    })();

  /**
   * Calculate unique images for each section to avoid reusing the same image
   */
  const sectionImages = (() => {
    const usedImages = new Set<string>();
    const sections = [
      "exteriorAndOutdoorSpaces",
      "entranceAndLayout",
      "technologyAndEnergy",
      "safetyAndMaterials",
    ] as const;

    return sections.reduce(
      (acc, sectionName) => {
        const image = selectUniqueImage({
          observations: analysis?.observations?.[sectionName]?.observations,
          mainImage: analysis?.observations?.[sectionName]?.mainImage,
          usedImages,
        });
        if (image) {
          usedImages.add(image);
        }
        acc[sectionName] = image;
        return acc;
      },
      {} as Record<(typeof sections)[number], string | undefined>,
    );
  })();

  const handleAnalysisIndexClick = (index: number) => {
    const section = document.getElementById(`analysisSection${index}`);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePrompt = (prompt: UserPromptRecord) => {
    onPrompt(prompt);
  };

  const handleObservationsClick: AnalysisObservationsSectionProps["onObservationsClick"] =
    (index: AnalysisSectionIndex) => {
      setActiveObservationIndex(index);
      setIsObservationModalOpen(true);
    };

  // const observationCategories = useMemo<AnalysisObservationCategory[]>(
  //   () => [
  //     {
  //       id: "environmentAndNeighborhood",
  //       name: "Omgeving & buurt",
  //       observations:
  //         analysis?.observations?.environmentAndNeighborhood?.observations,
  //     },
  //     {
  //       id: "exteriorAndOutdoorSpaces",
  //       name: "Exterieur & buitenruimtes",
  //     },
  //     {
  //       id: "entranceAndLayout",
  //       name: "Entree & indeling",
  //       observations: analysis?.observations?.entranceAndLayout?.observations,
  //     },
  //     {
  //       id: "technologyAndEnergy",
  //       name: "Techniek & energie",
  //       observations:
  //         analysis?.observations?.technologyAndEnergy?.observations,
  //     },
  //     {
  //       id: "safetyAndMaterials",
  //       name: "Veiligheid & materialen",
  //       observations:
  //         analysis?.observations?.safetyAndMaterials?.observations,
  //     },
  //     {
  //       id: "generalAndLegal",
  //       name: "Algemeen & juridisch",
  //       observations: analysis?.observations?.generalAndLegal?.observations,
  //     },
  //   ],
  //   [analysis?.observations],
  // );

  const isAnyLoading: boolean =
    analysis?.summary?.loading === true ||
    analysis?.gallery?.loading === true ||
    analysis?.neighbourhood?.loading === true ||
    analysis?.propertyValue?.loading === true ||
    analysis?.observations?.loading === true;

  const analysisIndexItems: AnalysisIndexProps["items"] = [
    // Observations
    {
      index: AnalysisSectionIndex.EnvironmentAndNeighborhood,
      title:
        analysisSectionIndexLabels[
          AnalysisSectionIndex.EnvironmentAndNeighborhood
        ],
      subtitle: formatInsightCount(
        analysis?.observations?.environmentAndNeighborhood?.observations
          ?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.environmentAndNeighborhood,
    },
    {
      index: AnalysisSectionIndex.ExteriorAndOutdoorSpaces,
      title:
        analysisSectionIndexLabels[
          AnalysisSectionIndex.ExteriorAndOutdoorSpaces
        ],
      subtitle: formatInsightCount(
        analysis?.observations?.exteriorAndOutdoorSpaces?.observations?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.exteriorAndOutdoorSpaces,
    },
    {
      index: AnalysisSectionIndex.EntranceAndLayout,
      title: analysisSectionIndexLabels[AnalysisSectionIndex.EntranceAndLayout],
      subtitle: formatInsightCount(
        analysis?.observations?.entranceAndLayout?.observations?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.entranceAndLayout,
    },
    {
      index: AnalysisSectionIndex.TechnologyAndEnergy,
      title:
        analysisSectionIndexLabels[AnalysisSectionIndex.TechnologyAndEnergy],
      subtitle: formatInsightCount(
        analysis?.observations?.technologyAndEnergy?.observations?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.technologyAndEnergy,
    },
    {
      index: AnalysisSectionIndex.SafetyAndMaterials,
      title:
        analysisSectionIndexLabels[AnalysisSectionIndex.SafetyAndMaterials],
      subtitle: formatInsightCount(
        analysis?.observations?.safetyAndMaterials?.observations?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.safetyAndMaterials,
    },
    {
      index: AnalysisSectionIndex.GeneralAndLegal,
      title: analysisSectionIndexLabels[AnalysisSectionIndex.GeneralAndLegal],
      subtitle: formatInsightCount(
        analysis?.observations?.generalAndLegal?.observations?.length,
      ),
      loading: analysis?.observations?.loading,
      noData:
        analysis?.observations?.loading === false &&
        !analysis?.observations?.generalAndLegal,
    },
    // Others
    {
      index: AnalysisSectionIndex.ALookInside,
      title: analysisSectionIndexLabels[AnalysisSectionIndex.ALookInside],
      subtitle: formatInsightCount(images?.length),
      loading: analysis?.gallery?.loading,
      noData: analysis?.gallery?.loading === false && !analysis?.gallery?.data,
    },
    {
      index: AnalysisSectionIndex.InTheNeighborhood,
      title: analysisSectionIndexLabels[AnalysisSectionIndex.InTheNeighborhood],
      subtitle: formatInsightCount(
        analysis?.neighbourhood?.data?.amenities?.length,
        "voorziening",
        "voorzieningen",
      ),
      loading: analysis?.neighbourhood?.loading,
      noData:
        analysis?.neighbourhood?.loading === false &&
        !analysis?.neighbourhood?.data,
    },
    {
      index: AnalysisSectionIndex.PropertyHazardProfile,
      title:
        analysisSectionIndexLabels[AnalysisSectionIndex.PropertyHazardProfile],
      subtitle: "1 inzicht",
      loading: analysis?.propertyHazardProfile?.loading,
      noData:
        analysis?.propertyHazardProfile?.loading === false &&
        !analysis?.propertyHazardProfile?.data,
    },
    {
      index: AnalysisSectionIndex.SunPosition,
      title: analysisSectionIndexLabels[AnalysisSectionIndex.SunPosition],
      subtitle: "1 inzicht",
    },
    {
      index: AnalysisSectionIndex.PropertyValueDevelopment,
      title:
        analysisSectionIndexLabels[
          AnalysisSectionIndex.PropertyValueDevelopment
        ],
      subtitle: "2 inzichten",
      loading: analysis?.propertyValue?.loading,
      noData:
        analysis?.propertyValue?.loading === false &&
        !analysis?.propertyValue?.data,
    },
    // {
    //   index: AnalysisSection.MaintenanceAndRenovation,
    //   title: "Onderhoud & renovatie",
    //   subtitle: "Coming soon",
    //   disabled: true,
    // },
    // {
    //   index: AnalysisSection.FinancingTheProperty,
    //   title: "Woning financieren",
    //   subtitle: "Coming soon",
    //   disabled: true,
    // },
  ];

  const noData: boolean =
    analysis?.summary?.loading === false && !analysis?.summary?.data;

  /**
   * Opens the map modal with a specific type
   */
  const openMapModal = (type: MapModalType = "general") => {
    setMapModalType(type);
    setIsMapModalOpen(true);
  };

  return (
    <>
      <Spacing height="m" />

      <div ref={ref}>
        <ContentMenuProvider>
          <Flex direction="column">
            <AnalysisSummary
              title="Woninganalyse"
              container="default"
              loading={isAnyLoading || analysis?.summary?.loading}
              summary={analysis?.summary?.data?.summary}
              grade={analysis?.summary?.data?.grade}
              disabled={lockUI}
              noData={noData}
              savable={savable}
              onSaveToggle={onSaveToggle}
              saved={saved}
              record={record}
              unlisted={unlisted}
              images={images ?? []}
              onImagesModalOpen={() => setImagesModalOpen(true)}
              interiorDesignerAction={interiorDesignerAction}
              CTAs={{
                maximumMortgage: CTAs?.maximumMortgage,
                chance: CTAs?.chance,
              }}
              loadingChildren={
                <AnalysisLoader
                  id={record.id}
                  description={`Huub is ${record.street} aan het analyseren. Dit kan enkele minuten duren.`}
                  steps={loaderSteps}
                />
              }
            >
              <Container>
                <Heading
                  variant="secondary"
                  title="De belangrijkste inzichten per onderdeel"
                />
                <Spacing />
                <AnalysisIndex
                  onItemClick={handleAnalysisIndexClick}
                  items={analysisIndexItems}
                />
                <Spacing />
              </Container>
            </AnalysisSummary>
          </Flex>

          <PageBreak />

          <ContentMenu variant={contentMenuVariant}>
            <PageBreak className={contentMenuPageBreakStyle} />
          </ContentMenu>

          <Spacing />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.EnvironmentAndNeighborhood}
            name={
              analysisSectionIndexLabels[
                AnalysisSectionIndex.EnvironmentAndNeighborhood
              ]
            }
            description={
              analysis?.observations?.environmentAndNeighborhood?.summary
            }
            illustration={
              analysisSectionIllustrations[
                AnalysisSectionIndex.EnvironmentAndNeighborhood
              ]
            }
            plusPoints={
              analysis?.observations?.environmentAndNeighborhood?.plusPoints
            }
            observations={
              analysis?.observations?.environmentAndNeighborhood?.observations
            }
            grade={analysis?.observations?.environmentAndNeighborhood?.grade}
            variant="pentaptych"
            main={
              <MapboxMapCard
                accessToken={mapBoxToken}
                center={center}
                controlsConfig={false}
                onClick={() => openMapModal("neighbourhood")}
                buttonValue="Open kaart"
              />
            }
            questions={
              analysis?.observations?.environmentAndNeighborhood?.questions
            }
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            loadingObservations={["Ligging", "Voorzieningen", "Parkeren"]}
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.environmentAndNeighborhood
            }
          />

          <PageBreak />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.ExteriorAndOutdoorSpaces}
            name="Exterieur & buitenruimtes"
            description={
              analysis?.observations?.exteriorAndOutdoorSpaces?.summary
            }
            illustration={
              analysisSectionIllustrations[
                AnalysisSectionIndex.ExteriorAndOutdoorSpaces
              ]
            }
            plusPoints={
              analysis?.observations?.exteriorAndOutdoorSpaces?.plusPoints
            }
            observations={
              analysis?.observations?.exteriorAndOutdoorSpaces?.observations
            }
            grade={analysis?.observations?.exteriorAndOutdoorSpaces?.grade}
            variant="pentaptych"
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            main={
              (analysis?.observations?.loading ||
                !!sectionImages.exteriorAndOutdoorSpaces) && (
                <AnalysisObservationMainImage
                  src={sectionImages.exteriorAndOutdoorSpaces}
                  alt="Exterieur & buitenruimtes"
                  loading={analysis?.observations?.loading}
                  onClick={() => {
                    handleObservationsClick(
                      AnalysisSectionIndex.ExteriorAndOutdoorSpaces,
                    );
                  }}
                />
              )
            }
            loadingObservations={["Gevels", "Dak", "Kozijnen"]}
            questions={
              analysis?.observations?.exteriorAndOutdoorSpaces?.questions
            }
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.exteriorAndOutdoorSpaces
            }
          />

          <PageBreak />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.EntranceAndLayout}
            name={
              analysisSectionIndexLabels[AnalysisSectionIndex.EntranceAndLayout]
            }
            description={analysis?.observations?.entranceAndLayout?.summary}
            illustration={
              analysisSectionIllustrations[
                AnalysisSectionIndex.EntranceAndLayout
              ]
            }
            plusPoints={analysis?.observations?.entranceAndLayout?.plusPoints}
            observations={
              analysis?.observations?.entranceAndLayout?.observations
            }
            grade={analysis?.observations?.entranceAndLayout?.grade}
            variant="pentaptych"
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            main={
              (analysis?.observations?.loading ||
                !!sectionImages.entranceAndLayout) && (
                <AnalysisObservationMainImage
                  src={sectionImages.entranceAndLayout}
                  alt="Entree & indeling"
                  loading={analysis?.observations?.loading}
                  onClick={() => {
                    handleObservationsClick(
                      AnalysisSectionIndex.EntranceAndLayout,
                    );
                  }}
                />
              )
            }
            loadingObservations={["Indeling", "Entree", "Woonkamer"]}
            questions={analysis?.observations?.entranceAndLayout?.questions}
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.entranceAndLayout
            }
          />

          <PageBreak />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.TechnologyAndEnergy}
            name={
              analysisSectionIndexLabels[
                AnalysisSectionIndex.TechnologyAndEnergy
              ]
            }
            description={analysis?.observations?.technologyAndEnergy?.summary}
            illustration={
              analysisSectionIllustrations[
                AnalysisSectionIndex.TechnologyAndEnergy
              ]
            }
            plusPoints={analysis?.observations?.technologyAndEnergy?.plusPoints}
            observations={
              analysis?.observations?.technologyAndEnergy?.observations
            }
            grade={analysis?.observations?.technologyAndEnergy?.grade}
            variant="pentaptych"
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            main={
              (analysis?.observations?.loading ||
                !!sectionImages.technologyAndEnergy) && (
                <AnalysisObservationMainImage
                  src={sectionImages.technologyAndEnergy}
                  alt="Techniek & energie"
                  loading={analysis?.observations?.loading}
                  onClick={() => {
                    handleObservationsClick(
                      AnalysisSectionIndex.TechnologyAndEnergy,
                    );
                  }}
                />
              )
            }
            loadingObservations={["Energielabel", "CV-ketel", "Isolatie"]}
            questions={analysis?.observations?.technologyAndEnergy?.questions}
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.technologyAndEnergy
            }
          />

          <PageBreak />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.SafetyAndMaterials}
            name={
              analysisSectionIndexLabels[
                AnalysisSectionIndex.SafetyAndMaterials
              ]
            }
            description={analysis?.observations?.safetyAndMaterials?.summary}
            illustration={
              analysisSectionIllustrations[
                AnalysisSectionIndex.SafetyAndMaterials
              ]
            }
            plusPoints={analysis?.observations?.safetyAndMaterials?.plusPoints}
            observations={
              analysis?.observations?.safetyAndMaterials?.observations
            }
            grade={analysis?.observations?.safetyAndMaterials?.grade}
            variant="pentaptych"
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            main={
              (analysis?.observations?.loading ||
                !!sectionImages.safetyAndMaterials) && (
                <AnalysisObservationMainImage
                  src={sectionImages.safetyAndMaterials}
                  alt="Veiligheid & materialen"
                  loading={analysis?.observations?.loading}
                  onClick={() => {
                    handleObservationsClick(
                      AnalysisSectionIndex.SafetyAndMaterials,
                    );
                  }}
                />
              )
            }
            loadingObservations={["Bouwmaterialen", "Asbest", "Fundering"]}
            questions={analysis?.observations?.safetyAndMaterials?.questions}
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.safetyAndMaterials
            }
          />

          <PageBreak />

          <AnalysisObservationsSection
            index={AnalysisSectionIndex.GeneralAndLegal}
            name={
              analysisSectionIndexLabels[AnalysisSectionIndex.GeneralAndLegal]
            }
            description={analysis?.observations?.generalAndLegal?.summary}
            illustration={
              analysisSectionIllustrations[AnalysisSectionIndex.GeneralAndLegal]
            }
            plusPoints={analysis?.observations?.generalAndLegal?.plusPoints}
            observations={analysis?.observations?.generalAndLegal?.observations}
            grade={analysis?.observations?.generalAndLegal?.grade}
            onPrompt={handlePrompt}
            onObservationsClick={handleObservationsClick}
            loading={analysis?.observations?.loading}
            loadingObservations={["Bouwjaar", "Eigendom", "Bestemmingsplan"]}
            questions={analysis?.observations?.generalAndLegal?.questions}
            noData={
              analysis?.observations?.loading === false &&
              !analysis?.observations?.generalAndLegal
            }
          />

          <PageBreak />

          <AnalysisGallery
            index={AnalysisSectionIndex.ALookInside}
            images={analysis?.gallery?.data?.images ?? []}
            container="default"
            title="Een kijkje binnen"
            description={analysis?.gallery?.data?.summary}
            loading={analysis?.gallery?.loading}
          />

          <PageBreak />

          <NeighbourhoodAnalysis
            index={AnalysisSectionIndex.InTheNeighborhood}
            title="In de buurt"
            address={record.street}
            center={center}
            accessToken={mapBoxToken}
            container="default"
            name={analysis?.neighbourhood?.data?.name}
            description={analysis?.neighbourhood?.data?.summary}
            amenities={analysis?.neighbourhood?.data?.amenities ?? []}
            loading={analysis?.neighbourhood?.loading}
            onViewAll={() => openMapModal("general")}
          />

          <PageBreak />

          <PropertyHazardProfileAnalysis
            index={AnalysisSectionIndex.PropertyHazardProfile}
            container="default"
            description={analysis?.propertyHazardProfile?.data?.summary}
            recommendations={
              analysis?.propertyHazardProfile?.data?.recommendations
            }
            riskLevel={analysis?.propertyHazardProfile?.data?.riskLevel}
            riskCategory={analysis?.propertyHazardProfile?.data?.riskCategory}
            soilType={analysis?.propertyHazardProfile?.data?.soilType}
            soilPercentage={
              analysis?.propertyHazardProfile?.data?.soilPercentage
            }
            derived={analysis?.propertyHazardProfile?.data?.derived}
            loading={analysis?.propertyHazardProfile?.loading}
            noData={
              analysis?.propertyHazardProfile?.loading === false &&
              !analysis?.propertyHazardProfile?.data
            }
            center={center}
            mapBoxToken={mapBoxToken}
            onMapClick={() => openMapModal("foundation")}
            getGeoJSON={getGeoJSON}
          />

          <PageBreak />

          <SundialAnalysis
            index={AnalysisSectionIndex.SunPosition}
            container="default"
            center={center}
            address={record.street}
            accessToken={mapBoxToken}
          />

          <PageBreak />

          <PropertyValueAnalysis
            index={AnalysisSectionIndex.PropertyValueDevelopment}
            container="default"
            currentPrice={record.price}
            squareMeterPrice={record.squareMeterPrice}
            wozPrices={analysis?.propertyValue?.data?.wozPrices}
            description={analysis?.propertyValue?.data?.summary}
            realisticOffer={
              <>
                <Text grow>
                  Ontdek samen met een adviseur wat een realistisch bod is —
                  onderbouwd met data, zodat je met vertrouwen kunt bieden.
                </Text>
                {CTAs?.realisticOffer}
              </>
            }
            loading={analysis?.propertyValue?.loading}
            noData={
              analysis?.propertyValue?.loading === false &&
              !analysis?.propertyValue?.data
            }
          />
        </ContentMenuProvider>

        {isObservationModalOpen &&
          activeObservationIndex !== undefined &&
          activeObservationCategory !== undefined && (
            <AnalysisObservationsModal
              category={activeObservationCategory}
              index={activeObservationIndex}
              onClose={() => setIsObservationModalOpen(false)}
            />
          )}

        {isImagesModalOpen && !!images && images.length > 0 && (
          <Modal
            placement="center"
            size="large"
            title={
              <Flex direction="column" gap="xxs">
                <Text variant="displayS" bold>
                  Fotogallerij
                </Text>
                <Text color="currentColor50">
                  {record.street}, {record.city}
                </Text>
              </Flex>
            }
            onClose={() => setImagesModalOpen(false)}
            escape
            closeable
          >
            <GalleryCarousel items={images ?? []} navigation="arrows" />
          </Modal>
        )}

        {isMapModalOpen && (
          <MapModal
            city={record.city}
            center={center}
            mapBoxToken={mapBoxToken}
            type={mapModalType}
            loading={isAnyLoading}
            onClose={() => setIsMapModalOpen(false)}
            getGeoJSON={getGeoJSON}
            address={record.street}
            amenities={analysis?.neighbourhood?.data?.amenities ?? []}
          />
        )}

        {(isAnyLoading || noData) && (
          <MessageBanner
            icon={isAnyLoading ? "loading" : "alertFilled"}
            body={
              isAnyLoading
                ? "Huub is de woning aan het analyseren. Dit duurt enkele minuten. Je krijgt een notificatie wanneer de analyse klaar is."
                : unlisted
                  ? "Huub kan deze woning nu niet goed analyseren omdat er te weinig of geen gegevens beschikbaar zijn."
                  : "Huub kan geen volledige analyse maken omdat de woning momenteel niet te koop staat."
            }
          />
        )}
      </div>
    </>
  );
};
